import { parsePhysiologicalMontioringCases } from '../services/parsePhysiologicalData';
import { getLastAndSecondLastScore  } from '../services/Patient';
import { getEnvironmentApiUrl } from '../services/General';

import PatientsContext from '../context/PatientsContext';
import FiltersContext from '../context/FiltersContext';
import ScoresContext from '../context/ScoresContext';

import { useEffect, useContext, useState, useCallback } from 'react';
import useHttp from './useHttp';

import { useTranslation } from 'react-i18next';

import moment from 'moment';

import { Case, Patient } from '../interfaces/Patient';
import { PollValues } from '../interfaces/Poll';
import { PhysiologicalMonitoring } from '../interfaces/PhysiologicalMonitoring';
import { getDefaultTimerMiliSeconds } from '../services/util';

interface CaseFilterData {
    case_id: Case['id'],
    start_date: string | null
    end_date: string | null
}

const TIMER = getDefaultTimerMiliSeconds();

const usePatientsData = () => {

    const { t } = useTranslation();

    const [isFirstLoading, setIsFirstLoading] = useState(true);

    const { patientFilters: { currentProfileID } } = useContext(FiltersContext);
    const { setPatients, setPhysiologicalData } = useContext(PatientsContext);
    const { scores } = useContext(ScoresContext);

    const { isLoading: isLoadingPatients, error: errorPatients, sendRequest: sendPatientListRequest } = useHttp<Patient[]>();

    const { error: errorInquiries, sendRequest: getInquiries } = useHttp<PollValues[]>();

    const { error: errorPhysiological, sendRequest: sendSonarRequest } = useHttp<PhysiologicalMonitoring[]>();
    const { error: errorSadValues, sendRequest: getLastSadValues } = useHttp<PhysiologicalMonitoring[]>();

    const parsePhysiologicalData = useCallback((data: PhysiologicalMonitoring[], inquiries: PollValues[], cases_ids: number[]) => {

        if (data.length !== 0 || inquiries.length !== 0) {

            const physiologicalMonitoring = parsePhysiologicalMontioringCases(data, inquiries, cases_ids);
            setPhysiologicalData(physiologicalMonitoring);

            setPatients(oldPatients => {
                return oldPatients.map(oldPatient => {

                    const newPatient = { ...oldPatient };

                    const caseID = oldPatient.cases.length > 0 ? oldPatient.cases[0].id : null;
                    const physiologicalCasePatient = physiologicalMonitoring.find(physiologicalCase => physiologicalCase.case_id === caseID) ?? null;

                    if (physiologicalCasePatient !== null) {
                        const keys = ['FC_EKG', 'PULSIOX', 'PA_S', 'PA_D', 'TEMP', 'WEIGHT'] as const;
                        const firstTimestamp = physiologicalCasePatient.elements.find(timestamp =>
                            keys.some(key => Object.keys(timestamp.values).includes(key)));
                        if (firstTimestamp) {
                            keys.forEach(key => newPatient[key] = firstTimestamp.values[key]);
                        }
                    }

                    const computedScores = scores?.map(score => ({
                        ...getLastAndSecondLastScore(physiologicalCasePatient?.elements ?? null, score),
                        score_id: score.id
                    })) ?? [];

                    newPatient.scores = computedScores;
                    newPatient.pps = physiologicalCasePatient?.elements.find(el => el.values['PPS'] !== undefined)?.values['PPS'];
                    const silencePactValue = physiologicalCasePatient?.elements.find(el => el.values['SILENCE_PACT'] !== undefined)?.values['SILENCE_PACT'];
                    newPatient.conspiracy_silence = silencePactValue === undefined ? undefined : silencePactValue === 1;
                    const sudAttentionComment = physiologicalCasePatient?.elements.find(el => el.comments['SOUTH_ATTENTION_comment'] !== undefined)?.comments['SOUTH_ATTENTION_comment'];
                    newPatient.sud_atention = sudAttentionComment === 'SOUTH_ATTENTION_HOME' ? t('home') : sudAttentionComment === 'SOUTH_ATTENTION_HOSPITAL' ? t('hospital') : '';
                    newPatient.support_resource = physiologicalCasePatient?.elements.find(el => el.comments['SUPPORT_RESOURCE_comment'] !== undefined)?.comments['SUPPORT_RESOURCE_comment'];
                    // newPatient.VIG = physiologicalCasePatient?.elements.find(el => el.values['VIG'] !== undefined)?.values['VIG'];
                    // const municipal = physiologicalCasePatient?.elements.find(el => el.comments['SAD_MUNICIPAL'] !== undefined)?.comments['SAD_MUNICIPAL'];
                    // newPatient.municipal = municipal;
                    // const teleassistance = physiologicalCasePatient?.elements.find(el => el.values['TELEASISTENCIA'] !== undefined)?.values['TELEASISTENCIA'];
                    // newPatient.teleassistance = teleassistance === 1 ? true : teleassistance === 0 ? false : null;
                    return newPatient;
                });
            });
        }
        else setPhysiologicalData([]);
    }, [scores, setPatients, setPhysiologicalData, t]);

    const parseInquiries = useCallback((inquiries: PollValues[], case_filter_data: CaseFilterData[]) => {

        sendSonarRequest({
            url: getEnvironmentApiUrl() + 'physiological_monitoring/all_case_list',
            method: 'POST',
            body: {
                usePatientId: false,
                case_filter_data,
                params: {},
                filter: {},
            },
        }, (data) => parsePhysiologicalData(data, inquiries, case_filter_data.map(csd => csd.case_id)));

        case_filter_data.forEach(caseData => {
            getLastSadValues({
                url: getEnvironmentApiUrl() + 'physiological_monitoring/get_last_before',
                method: 'POST',
                body: {
                    threshold_seconds: 0,
                    sheet_param_ids: ['VIG', 'SAD_MUNICIPAL', 'TELEASISTENCIA'],
                    date: new Date().toISOString(),
                    case_id: caseData.case_id,
                }
            }, (data) => {
                if (data.length === 0) return;
                const groupedDataByPatient: { [id: string]: PhysiologicalMonitoring[] } = {};
                for (const phys of data) {
                    if (phys.patient_id === undefined) continue;
                    const id = phys.patient_id.toString();
                    if (!groupedDataByPatient[id]) groupedDataByPatient[id] = [phys];
                    else groupedDataByPatient[id].push(phys)
                }
                setPatients(oldPatients => oldPatients.map(p => {
                    if (!groupedDataByPatient[p.id]) return p;
                    const monitorings = groupedDataByPatient[p.id];
                    const newPatient = {...p};
                    monitorings.forEach(phys => {
                        if(phys.sheet_parameter_id === 'VIG') newPatient.VIG = Number(phys.value);
                        if(phys.sheet_parameter_id === 'SAD_MUNICIPAL') newPatient.municipal = phys.sheet_parameter_option_id;
                        if(phys.sheet_parameter_id === 'TELEASISTENCIA') newPatient.teleassistance = Number(phys.value) === 1 ? true : Number(phys.value) === 0 ? false : null;
                    })
                    return newPatient;
                }))
            })
        })

    }, [sendSonarRequest, parsePhysiologicalData, getLastSadValues, setPatients]);

    const parsePatients = useCallback((patients: Patient[]) => {
        setPatients(oldPatients => {
            return patients.map(patient => {
                const oldPatient = oldPatients.find(p => p.id === patient.id);
                if (oldPatient === undefined) return patient;

                const { FC_EKG, PULSIOX, PA_S, PA_D, TEMP, WEIGHT, scores, pps, sud_atention,
                    conspiracy_silence, support_resource, VIG, teleassistance, municipal } = oldPatient;

                const newPatient = {
                    ...patient, FC_EKG, PULSIOX, PA_S, PA_D, TEMP, WEIGHT, scores, pps,
                    sud_atention, conspiracy_silence, support_resource, VIG, teleassistance, municipal
                };

                return newPatient;
            });
        });

        setIsFirstLoading(false);

        const case_filter_data: CaseFilterData[] = [];
        patients.forEach(patient => {
            if (patient.cases.length > 0 && patient.cases[0].follow_ups && patient.cases[0].follow_ups.length > 0) {
                const lastWeekDate = moment().subtract(7, 'days');
                const startDate = patient.cases[0].start_date;
                const endDate = patient.cases[0].end_date;
                const endDateInThisWeek = endDate === null || endDate === undefined ? true
                    : moment(endDate).isAfter(lastWeekDate);
                if (endDateInThisWeek && startDate) {
                    const startDateMax7Days = moment(startDate).isAfter(lastWeekDate) ? startDate : lastWeekDate.toISOString();
                    case_filter_data.push(({
                        case_id: patient.cases[0].id,
                        start_date: startDateMax7Days as string,
                        end_date: (endDate || new Date().toISOString()) as string,
                    }));
                }
            }
        });

        getInquiries({
            url: getEnvironmentApiUrl() + 'patient/inquiries_answers',
            method: 'POST',
            body: { case_filter_data },
        }, (data) => parseInquiries(data, case_filter_data));
    }, [getInquiries, parseInquiries, setPatients]);

    const getPatients = useCallback(() => {
        // if (currentProfileID === null) return;
        sendPatientListRequest({
            url: getEnvironmentApiUrl() + 'patient/hospital_list',
            method: 'POST',
            body: {
                profile_id: currentProfileID,
                params: {},
                filter: {}
            },
        }, parsePatients);
    }, [sendPatientListRequest, parsePatients, currentProfileID]);

    useEffect(() => {
        if (scores === null) return;
        getPatients();
        const timer = setInterval(() => {
            getPatients();
        }, TIMER);

        return () => { clearInterval(timer); };
    }, [scores, getPatients]);

    const loading = isLoadingPatients && isFirstLoading;
    const error = errorPatients || errorInquiries || errorPhysiological;

    return { loading, error };

};

export default usePatientsData;